import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, effect, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ISbStoryData } from '@storyblok/js';
import { DynamicComponent, DynamicIoDirective } from 'ng-dynamic-component';
import { TranslateLinkPipe } from '../../../pipes/translate-link.pipe';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { StoryblokLoadComponentsService } from '../../../services/storyblok-load-components.service';
import { NotFoundComponent } from '../../../shared/components/not-found/not-found.component';
import { WebsiteFooterComponent } from '../../../shared/components/website-footer/website-footer.component';
import { WebsiteHeaderComponent } from '../../../shared/components/website-header/website-header.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { StoryblokCdnPipe } from '../../pipes/storyblok-cdn.pipe';
import { StoryblokCta, StoryblokImage } from '../../storyblok-types';
import { StoryblokDirective } from '../../storyblok.directive';
import { PreFooterComponent } from '../pre-footer/pre-footer.component';
import { TeaserAssetLoadingStore, computeTeaserAssetLoading } from '../teaser/teaser-asset-loading';

@Component({
  selector: 'app-storyblok-root',
  imports: [
    StoryblokDirective,
    DynamicComponent,
    DynamicIoDirective,
    WebsiteHeaderComponent,
    PreFooterComponent,
    RouterLink,
    ButtonDirective,
    WebsiteFooterComponent,
    NotFoundComponent,
    StoryblokCdnPipe,
    TranslatePipe,
    TranslateLinkPipe,
  ],
  templateUrl: './storyblok-root.component.html',
  styleUrls: ['./storyblok-root.component.scss'],
  providers: [TeaserAssetLoadingStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class StoryblokRootComponent {
  private storyblokLoadComponentsService = inject(StoryblokLoadComponentsService);

  readonly story = input.required<ISbStoryData>();
  readonly prefooter = input.required<{
    cta: StoryblokCta[];
    _uid: string;
    image: StoryblokImage;
    component: 'pre_footer';
    _editable: string;
  }>();
  readonly overlayNav = input.required<boolean>();
  readonly extraDataMap = input<
    Map<
      string,
      {
        [key: string]: unknown;
      }
    >
  >(undefined);
  readonly hideHeaderNav = input<boolean>(undefined);
  readonly hideHeader = input(false);
  readonly hideFooter = input(false);
  readonly customCta = input<'contact' | undefined>(undefined);
  /** Overrides the story theme — used when embedded inside the themed app. */
  readonly themeOverride = input<'light' | 'dark'>(undefined);
  /**
   * Drop CMS page/section `bg-base*` surfaces so content inherits the host
   * page (in-app embeds: rewards FAQ, pricing FAQ, etc.).
   */
  readonly transparentSurfaces = input(false);

  readonly pageTheme = computed<'light' | 'dark'>(() => {
    return this.themeOverride() ?? (this.story()?.content?.theme === 'dark' ? 'dark' : 'light');
  });

  readonly components = this.storyblokLoadComponentsService.components;

  /**
   * Page surface style. Empty/missing CMS value defaults to `base` — Storyblok
   * `default_value` is not applied on read for existing stories.
   */
  readonly pageStyle = computed<'base' | 'base-subdued' | 'base-strong'>(() => {
    const story = this.story();
    if (!story) return 'base';
    const style = story.content?.style;
    if (style === 'base-subdued' || style === 'base-strong' || style === 'base') return style;
    return 'base';
  });

  /** Rest/SSR nav chrome. Empty → page theme. Legacy `*-alpha` → light/dark. */
  readonly navTheme = computed<'light' | 'dark'>(() => {
    const story = this.story();
    if (!story) return 'light';
    const navTheme = story.content?.navTheme;
    const pageTheme = story.content?.theme;
    if (navTheme === 'dark' || navTheme === 'dark-alpha') return 'dark';
    if (navTheme === 'light' || navTheme === 'light-alpha') return 'light';
    return pageTheme === 'dark' ? 'dark' : 'light';
  });

  /** Effective dark chrome for the website footer / pre-footer. */
  readonly footerIsDark = computed<boolean>(() => {
    const story = this.story();
    if (!story) return false;

    const footerTheme = story.content?.footerTheme;
    const pageTheme = story.content?.theme;
    if (footerTheme === 'dark') return true;
    if (footerTheme === 'light') return false;
    return pageTheme === 'dark';
  });

  /** Pre-footer follows `footerTheme` (same as site footer). */
  readonly preFooterIsDark = computed<boolean>(() => {
    return this.footerIsDark();
  });

  private readonly teaserAssetLoading = inject(TeaserAssetLoadingStore);
  private readonly syncTeaserAssetLoading = effect(() => {
    this.teaserAssetLoading.modes.set(computeTeaserAssetLoading(this.story()?.content?.['body']));
  });

  dynamicInputs(values: { [key: string]: unknown }) {
    const data = { ...values };
    const extraData = this.extraDataMap()?.get((values as { component: string }).component);
    if (extraData) {
      for (const key of Object.keys(extraData)) data[key] = extraData[key];
    }
    return data;
  }
}
